import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  TimePicker,
  message,
  Space,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Transfer,
  Select,
  Divider,
  Badge,
  Tooltip,
  Empty,
  Steps,
  Alert,
  InputNumber,
  Switch,
  Checkbox,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  PlayCircleOutlined,
  OrderedListOutlined,
  CalendarOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../services/api";
import LoadingSpinner from "../components/UI/LoadingSpinner";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;

const DAY_OPTIONS = [
  { label: "จ", value: 0 },
  { label: "อ", value: 1 },
  { label: "พ", value: 2 },
  { label: "พฤ", value: 3 },
  { label: "ศ", value: 4 },
  { label: "ส", value: 5 },
  { label: "อา", value: 6 },
];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

const formatDaysShort = (days) => {
  if (!days || days.length === 0) return "-";
  if (days.length === 7) return "ทุกวัน";
  const sorted = [...days].sort((a, b) => a - b);
  const labels = sorted.map((d) => DAY_OPTIONS[d].label);
  // Common presets
  if (JSON.stringify(sorted) === JSON.stringify([0, 1, 2, 3, 4])) return "จ-ศ";
  if (JSON.stringify(sorted) === JSON.stringify([5, 6])) return "ส-อา";
  return labels.join(" ");
};

const PatrolRounds = () => {
  const { user } = useAuth();
  const [rounds, setRounds] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRound, setEditingRound] = useState(null);
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCheckpoints, setSelectedCheckpoints] = useState([]);
  const [expandedCheckpoints, setExpandedCheckpoints] = useState({});

  // Remove sample data - will use real API

  useEffect(() => {
    if (user?.current_company_id) {
      fetchData();
    }
  }, [user?.current_company_id]); // Re-fetch when company changes

  const fetchData = async () => {
    try {
      setLoading(true);

      const [roundsRes, checkpointsRes] = await Promise.all([
        api.get("/patrol-rounds"),
        api.get("/checkpoints"),
      ]);

      if (roundsRes.data.success) {
        setRounds(
          roundsRes.data.data.map((round) => ({
            ...round,
            active: round.is_active,
            checkpoints_count: round.checkpoints_count,
          })),
        );
      }

      if (checkpointsRes.data.success) {
        setCheckpoints(
          checkpointsRes.data.data.map((cp) => ({
            ...cp,
            key: cp.qr_code,
          })),
        );
      }
    } catch (error) {
      console.error("Fetch error:", error);
      message.error("ไม่สามารถโหลดข้อมูลได้");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRound(null);
    form.resetFields();
    setCurrentStep(0);
    setSelectedCheckpoints([]);
    setModalVisible(true);
  };

  const handleEdit = async (round) => {
    try {
      // Get detailed round data including checkpoints
      const response = await api.get(`/patrol-rounds/${round.id}`);
      if (response.data.success) {
        const detailedRound = response.data.data;

        setEditingRound(detailedRound);
        form.setFieldsValue({
          name: detailedRound.name,
          description: detailedRound.description,
          start_time: dayjs(detailedRound.start_time, "HH:mm"),
          end_time: dayjs(detailedRound.end_time, "HH:mm"),
          estimated_duration: detailedRound.estimated_duration,
          guards_assigned: detailedRound.guards_assigned,
          active: detailedRound.is_active,
          days_of_week: detailedRound.days_of_week || ALL_DAYS,
        });
        setSelectedCheckpoints(
          detailedRound.checkpoints?.map((cp) => cp.id) || [],
        );
        setCurrentStep(0);
        setModalVisible(true);
      }
    } catch (error) {
      console.error("Fetch round details error:", error);
      message.error("ไม่สามารถโหลดข้อมูลรอบตรวจได้");
    }
  };

  const handleDuplicate = async (round) => {
    try {
      // Get detailed round data including checkpoints
      const response = await api.get(`/patrol-rounds/${round.id}`);
      if (response.data.success) {
        const detailedRound = response.data.data;

        setEditingRound(null);
        form.setFieldsValue({
          name: `${detailedRound.name} (สำเนา)`,
          description: detailedRound.description,
          start_time: dayjs(detailedRound.start_time, "HH:mm"),
          end_time: dayjs(detailedRound.end_time, "HH:mm"),
          estimated_duration: detailedRound.estimated_duration,
          guards_assigned: detailedRound.guards_assigned,
          active: false,
          days_of_week: detailedRound.days_of_week || ALL_DAYS,
        });
        setSelectedCheckpoints(
          detailedRound.checkpoints?.map((cp) => cp.id) || [],
        );
        setCurrentStep(0);
        setModalVisible(true);
      }
    } catch (error) {
      console.error("Fetch round details error:", error);
      message.error("ไม่สามารถโหลดข้อมูลรอบตรวจได้");
    }
  };

  const handleSubmit = async (values) => {
    try {
      console.log("Form values received:", values);

      // Get all form values including those from previous steps
      const allFormValues = form.getFieldsValue(true);
      console.log("All form values:", allFormValues);

      if (selectedCheckpoints.length === 0) {
        message.warning("กรุณาเลือกจุดตรวจอย่างน้อย 1 จุด");
        return;
      }

      // Use all form values instead of relying on submitted values only
      const startTime = allFormValues.start_time;
      const endTime = allFormValues.end_time;

      if (!startTime || !endTime) {
        console.error("Time values missing:", {
          start_time: startTime,
          end_time: endTime,
          allFormValues: allFormValues,
        });
        message.error("กรุณาระบุเวลาเริ่มต้นและสิ้นสุด");
        return;
      }

      const payload = {
        name: allFormValues.name,
        description: allFormValues.description,
        start_time:
          startTime && startTime.format ? startTime.format("HH:mm") : startTime,
        end_time: endTime && endTime.format ? endTime.format("HH:mm") : endTime,
        estimated_duration: allFormValues.estimated_duration,
        guards_assigned: allFormValues.guards_assigned,
        active: allFormValues.active,
        days_of_week: allFormValues.days_of_week || ALL_DAYS,
        checkpoint_ids: selectedCheckpoints,
      };

      console.log("Submitting payload:", payload);
      console.log("Selected checkpoints:", selectedCheckpoints);
      console.log("Start time format check:", {
        original: allFormValues.start_time,
        formatted:
          startTime && startTime.format ? startTime.format("HH:mm") : startTime,
      });
      console.log("End time format check:", {
        original: allFormValues.end_time,
        formatted:
          endTime && endTime.format ? endTime.format("HH:mm") : endTime,
      });

      if (editingRound) {
        const response = await api.put(
          `/patrol-rounds/${editingRound.id}`,
          payload,
        );
        console.log("Update response:", response.data);
      } else {
        const response = await api.post("/patrol-rounds", payload);
        console.log("Create response:", response.data);
      }

      message.success(`รอบตรวจ${editingRound ? "แก้ไข" : "สร้าง"}สำเร็จ!`);
      setModalVisible(false);
      fetchData();
    } catch (error) {
      console.error("Submit error:", error);
      message.error(error.response?.data?.message || "การบันทึกล้มเหลว");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/patrol-rounds/${id}`);
      message.success("ลบรอบตรวจสำเร็จ!");
      fetchData();
    } catch (error) {
      console.error("Delete error:", error);
      message.error(error.response?.data?.message || "การลบล้มเหลว");
    }
  };

  const toggleRoundStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/patrol-rounds/${id}/toggle`);
      message.success(
        `${currentStatus ? "ปิดใช้งาน" : "เปิดใช้งาน"}รอบตรวจสำเร็จ!`,
      );
      fetchData();
    } catch (error) {
      console.error("Toggle status error:", error);
      message.error(error.response?.data?.message || "การเปลี่ยนสถานะล้มเหลว");
    }
  };

  const nextStep = () => {
    // Validate only fields for current step
    let fieldsToValidate = [];

    if (currentStep === 0) {
      fieldsToValidate = [
        "name",
        "start_time",
        "end_time",
        "estimated_duration",
        "guards_assigned",
        "days_of_week",
      ];
    } else if (currentStep === 1) {
      // No form validation needed for step 1 (just checkpoint selection)
      if (selectedCheckpoints.length === 0) {
        message.warning("กรุณาเลือกจุดตรวจอย่างน้อย 1 จุด");
        return;
      }
    }

    if (fieldsToValidate.length > 0) {
      form
        .validateFields(fieldsToValidate)
        .then(() => {
          setCurrentStep(currentStep + 1);
        })
        .catch((info) => {
          console.log("Validate Failed:", info);
        });
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const calculateDuration = () => {
    // Get current form values
    const startTime = form.getFieldValue("start_time");
    const endTime = form.getFieldValue("end_time");

    if (startTime && endTime) {
      // Calculate difference in minutes
      const start = startTime.hour() * 60 + startTime.minute();
      let end = endTime.hour() * 60 + endTime.minute();

      // Handle overnight shifts (end time is next day)
      if (end < start) {
        end += 24 * 60; // Add 24 hours in minutes
      }

      const durationMinutes = end - start;

      // Set the calculated duration
      if (durationMinutes > 0) {
        form.setFieldsValue({ estimated_duration: durationMinutes });
      }
    }
  };

  const checkTimeOverlap = (startTime, endTime) => {
    if (!startTime || !endTime) return null;

    const newStart = startTime.format("HH:mm:ss");
    const newEnd = endTime.format("HH:mm:ss");
    const currentRoundId = editingRound?.id;
    const currentDays = form.getFieldValue("days_of_week") || ALL_DAYS;
    const currentMask = currentDays.reduce((acc, d) => acc | (1 << d), 0);

    for (const round of rounds) {
      if (currentRoundId && round.id === currentRoundId) continue;
      if (!round.is_active) continue;

      // Skip if no common days
      const roundDays = round.days_of_week || ALL_DAYS;
      const roundMask = roundDays.reduce((acc, d) => acc | (1 << d), 0);
      if ((currentMask & roundMask) === 0) continue;

      const existingStart = round.start_time;
      const existingEnd = round.end_time;
      const overlaps = newStart < existingEnd && newEnd > existingStart;

      if (overlaps) {
        return {
          conflictRound: round,
          message: `เวลาทับซ้อนกับรอบ "${round.name}" (${existingStart.substring(0, 5)} - ${existingEnd.substring(0, 5)})`,
        };
      }
    }

    return null;
  };

  const toggleCheckpointExpansion = (roundId) => {
    setExpandedCheckpoints((prev) => ({
      ...prev,
      [roundId]: !prev[roundId],
    }));
  };

  const getStatusColor = (active) => (active ? "success" : "default");
  const getStatusText = (active) => (active ? "เปิดใช้งาน" : "ปิดใช้งาน");

  const transferDataSource = checkpoints.map((cp) => ({
    key: cp.id,
    title: cp.name,
    description: cp.key,
  }));

  const columns = [
    {
      title: "รอบตรวจ",
      key: "round_info",
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Space>
            <Text strong style={{ fontSize: 16 }}>
              {record.name}
            </Text>
            <Badge
              status={record.active ? "success" : "default"}
              text={getStatusText(record.active)}
            />
          </Space>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {record.description}
          </Text>
        </Space>
      ),
      width: 280,
    },
    {
      title: "เวลา",
      key: "schedule",
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Space>
            <ClockCircleOutlined style={{ color: "#1890ff" }} />
            <Text>
              {record.start_time} - {record.end_time}
            </Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ระยะเวลา: {record.estimated_duration} นาที
          </Text>
        </Space>
      ),
      width: 160,
    },
    {
      title: "วันที่ใช้งาน",
      key: "days",
      render: (_, record) => (
        <Tag color={record.days_of_week?.length === 7 ? "blue" : "purple"}>
          {formatDaysShort(record.days_of_week)}
        </Tag>
      ),
      width: 110,
      align: "center",
    },
    {
      title: "จุดตรวจ",
      key: "checkpoints",
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Space>
            <EnvironmentOutlined style={{ color: "#52c41a" }} />
            <Text strong>{record.checkpoints_count} จุด</Text>
            {record.checkpoints_count > 0 && (
              <Button
                type="text"
                size="small"
                icon={
                  expandedCheckpoints[record.id] ? (
                    <EyeInvisibleOutlined />
                  ) : (
                    <EyeOutlined />
                  )
                }
                onClick={() => toggleCheckpointExpansion(record.id)}
                style={{ padding: "0 4px" }}
              />
            )}
          </Space>
          {expandedCheckpoints[record.id] && (
            <Space wrap size={4}>
              {record.checkpoints?.map((cp) => (
                <Tag key={cp.id} size="small" color="blue">
                  {cp.order}. {cp.name}
                </Tag>
              ))}
            </Space>
          )}
        </Space>
      ),
      width: 240,
    },
    {
      title: "รปภ.",
      key: "guards",
      render: (_, record) => (
        <Space>
          <TeamOutlined style={{ color: "#fa8c16" }} />
          <Text>{record.guards_assigned} คน</Text>
        </Space>
      ),
      width: 100,
      align: "center",
    },
    {
      title: "การจัดการ",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="แก้ไข">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="คัดลอก">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleDuplicate(record)}
            />
          </Tooltip>
          <Tooltip title={record.active ? "ปิดใช้งาน" : "เปิดใช้งาน"}>
            <Button
              type="text"
              icon={
                record.active ? (
                  <ExclamationCircleOutlined />
                ) : (
                  <PlayCircleOutlined />
                )
              }
              onClick={() => toggleRoundStatus(record.id, record.active)}
              style={{ color: record.active ? "#fa8c16" : "#52c41a" }}
            />
          </Tooltip>
          <Popconfirm
            title="ต้องการลบรอบตรวจนี้?"
            description="การลบจะไม่สามารถย้อนกลับได้"
            onConfirm={() => handleDelete(record.id)}
            okText="ลบ"
            cancelText="ยกเลิก"
            okType="danger"
          >
            <Tooltip title="ลบ">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
      width: 160,
      align: "center",
    },
  ];

  const stepItems = [
    {
      title: "ข้อมูลพื้นฐาน",
      icon: <SettingOutlined />,
      description: "กำหนดชื่อ เวลา และรายละเอียด",
    },
    {
      title: "เลือกจุดตรวจ",
      icon: <EnvironmentOutlined />,
      description: "เลือกจุดที่ต้องตรวจในรอบนี้",
    },
    {
      title: "ตรวจสอบ",
      icon: <CheckCircleOutlined />,
      description: "ตรวจสอบข้อมูลก่อนบันทึก",
    },
  ];

  if (loading) {
    return <LoadingSpinner tip="กำลังโหลดข้อมูลรอบตรวจ..." />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title
            level={2}
            style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
          >
            <OrderedListOutlined style={{ color: "#1890ff" }} />
            จัดการรอบตรวจ
          </Title>
          <Paragraph
            type="secondary"
            style={{ margin: "8px 0 0 0", fontSize: 14 }}
          >
            กำหนดรอบเวลาตรวจ จุดที่ต้องตรวจ และการจัดสรรรปภ.
          </Paragraph>
        </Col>
        <Col>
          <Space>
            {/* <Button 
              icon={<CalendarOutlined />} 
              onClick={() => message.info('ฟีเจอร์กำหนดตารางจะเปิดใช้งานเร็วๆ นี้')}>
              ตารางการตรวจ
            </Button> */}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="large"
            >
              สร้างรอบตรวจใหม่
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <Title
                level={3}
                style={{ color: "#52c41a", margin: "0 0 8px 0" }}
              >
                {rounds.filter((r) => r.active).length}
              </Title>
              <Text type="secondary">รอบที่เปิดใช้งาน</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <Title
                level={3}
                style={{ color: "#1890ff", margin: "0 0 8px 0" }}
              >
                {rounds.reduce((sum, r) => sum + r.checkpoints_count, 0)}
              </Title>
              <Text type="secondary">จุดตรวจทั้งหมด</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <Title
                level={3}
                style={{ color: "#fa8c16", margin: "0 0 8px 0" }}
              >
                {rounds.reduce((sum, r) => sum + r.guards_assigned, 0)}
              </Title>
              <Text type="secondary">รปภ.ที่มอบหมาย</Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Card>
        {rounds.length > 0 ? (
          <Table
            columns={columns}
            dataSource={rounds}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} จาก ${total} รอบ`,
            }}
            locale={{
              emptyText: <Empty description="ไม่พบรอบตรวจ" />,
            }}
          />
        ) : (
          <Empty
            description={
              <span>
                ยังไม่มีรอบตรวจ <br />
                <Text type="secondary">เริ่มต้นสร้างรอบตรวจแรกของคุณ</Text>
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              สร้างรอบตรวจใหม่
            </Button>
          </Empty>
        )}
      </Card>

      <Modal
        title={
          <Space>
            <OrderedListOutlined />
            {editingRound ? "แก้ไขรอบตรวจ" : "สร้างรอบตรวจใหม่"}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Steps
          current={currentStep}
          items={stepItems}
          style={{ marginBottom: 32 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={(errorInfo) => {
            console.log("Form validation failed:", errorInfo);
          }}
          requiredMark={false}
        >
          {currentStep === 0 && (
            <div>
              <Alert
                message="กำหนดข้อมูลพื้นฐานของรอบตรวจ"
                description="ระบุชื่อ เวลา และรายละเอียดเพื่อให้รปภ.เข้าใจง่าย"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item
                    name="name"
                    label="ชื่อรอบตรวจ"
                    rules={[
                      { required: true, message: "กรุณาระบุชื่อรอบตรวจ" },
                      { min: 3, message: "ชื่อต้องมีอย่างน้อย 3 ตัวอักษร" },
                    ]}
                  >
                    <Input
                      placeholder="เช่น รอบเช้า, รอบบ่าย, รอบกลางคืน"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="active"
                    label="สถานะ"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Switch
                      checkedChildren="เปิดใช้งาน"
                      unCheckedChildren="ปิดใช้งาน"
                      size="default"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="description" label="คำอธิบาย (ไม่จำเป็น)">
                <Input.TextArea
                  placeholder="อธิบายจุดประสงค์หรือข้อกำหนดพิเศษของรอบตรวจนี้"
                  rows={3}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="start_time"
                    label="เวลาเริ่มต้น"
                    rules={[
                      { required: true, message: "กรุณาเลือกเวลาเริ่มต้น" },
                      {
                        validator: (_, value) => {
                          const endTime = form.getFieldValue("end_time");
                          if (value && endTime) {
                            const overlap = checkTimeOverlap(value, endTime);
                            if (overlap) {
                              return Promise.reject(new Error(overlap.message));
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <TimePicker
                      format="HH:mm"
                      placeholder="เลือกเวลา"
                      size="large"
                      style={{ width: "100%" }}
                      onChange={(time) => {
                        calculateDuration();
                        // Trigger validation for end_time as well
                        form.validateFields(["end_time"]);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="end_time"
                    label="เวลาสิ้นสุด"
                    rules={[
                      { required: true, message: "กรุณาเลือกเวลาสิ้นสุด" },
                      {
                        validator: (_, value) => {
                          const startTime = form.getFieldValue("start_time");
                          if (startTime && value) {
                            // Check if start time is before end time
                            if (
                              startTime.format("HH:mm") >= value.format("HH:mm")
                            ) {
                              return Promise.reject(
                                new Error("เวลาสิ้นสุดต้องมากกว่าเวลาเริ่มต้น"),
                              );
                            }

                            const overlap = checkTimeOverlap(startTime, value);
                            if (overlap) {
                              return Promise.reject(new Error(overlap.message));
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <TimePicker
                      format="HH:mm"
                      placeholder="เลือกเวลา"
                      size="large"
                      style={{ width: "100%" }}
                      onChange={(time) => {
                        calculateDuration();
                        // Trigger validation for start_time as well
                        form.validateFields(["start_time"]);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="estimated_duration"
                    label="ระยะเวลาโดยประมาณ (นาที)"
                    rules={[{ required: true, message: "กรุณาระบุระยะเวลา" }]}
                  >
                    <InputNumber
                      placeholder="120"
                      min={15}
                      max={480}
                      size="large"
                      style={{ width: "100%" }}
                      formatter={(value) => `${value} นาที`}
                      parser={(value) => value.replace(" นาที", "")}
                      readOnly
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="guards_assigned"
                label="จำนวนรปภ.ที่มอบหมาย"
                rules={[{ required: true, message: "กรุณาระบุจำนวนรปภ." }]}
              >
                <InputNumber
                  placeholder="1"
                  min={1}
                  max={10}
                  size="large"
                  style={{ width: 200 }}
                  formatter={(value) => `${value} นาย`}
                  parser={(value) => value.replace("นาย", "")}
                />
              </Form.Item>

              <Form.Item
                name="days_of_week"
                label="วันที่ใช้งาน"
                initialValue={ALL_DAYS}
                rules={[
                  {
                    validator: (_, value) =>
                      value && value.length > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error("กรุณาเลือกอย่างน้อย 1 วัน")),
                  },
                ]}
              >
                <Checkbox.Group
                  options={DAY_OPTIONS}
                  onChange={() => {
                    const st = form.getFieldValue("start_time");
                    const et = form.getFieldValue("end_time");
                    if (st && et) form.validateFields(["start_time", "end_time"]);
                  }}
                />
              </Form.Item>
              <Space style={{ marginTop: -16, marginBottom: 16 }}>
                <Button
                  size="small"
                  onClick={() => form.setFieldValue("days_of_week", ALL_DAYS)}
                >
                  ทุกวัน
                </Button>
                <Button
                  size="small"
                  onClick={() =>
                    form.setFieldValue("days_of_week", [0, 1, 2, 3, 4])
                  }
                >
                  จ-ศ
                </Button>
                <Button
                  size="small"
                  onClick={() => form.setFieldValue("days_of_week", [5, 6])}
                >
                  ส-อา
                </Button>
              </Space>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Alert
                message="เลือกจุดตรวจสำหรับรอบนี้"
                description="ลากจุดที่ต้องการจากด้านซ้ายไปด้านขวา ลำดับจะถูกกำหนดอัตโนมัติ"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Transfer
                dataSource={transferDataSource}
                targetKeys={selectedCheckpoints}
                onChange={setSelectedCheckpoints}
                render={(item) => (
                  <Space direction="vertical" size={0}>
                    <Text strong>{item.title}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.description}
                    </Text>
                  </Space>
                )}
                titles={["จุดตรวจที่มี", "จุดตรวจในรอบนี้"]}
                listStyle={{
                  width: 350,
                  height: 400,
                }}
                operations={["เลือก", "ยกเลิก"]}
                locale={{
                  itemUnit: "จุด",
                  itemsUnit: "จุด",
                  searchPlaceholder: "ค้นหาจุดตรวจ",
                  notFoundContent: "ไม่พบจุดตรวจ",
                }}
                showSearch
                filterOption={(inputValue, item) =>
                  item.title.toLowerCase().includes(inputValue.toLowerCase()) ||
                  item.description
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                }
              />
            </div>
          )}

          {currentStep === 2 && (
            <div>
              {(() => {
                const startTime = form.getFieldValue("start_time");
                const endTime = form.getFieldValue("end_time");
                const overlap =
                  startTime && endTime
                    ? checkTimeOverlap(startTime, endTime)
                    : null;

                if (overlap) {
                  return (
                    <Alert
                      message="เตือน: เวลาทับซ้อน"
                      description={overlap.message}
                      type="warning"
                      showIcon
                      style={{ marginBottom: 24 }}
                    />
                  );
                }

                return (
                  <Alert
                    message="ตรวจสอบข้อมูลก่อนบันทึก"
                    type="success"
                    showIcon
                    style={{ marginBottom: 24 }}
                  />
                );
              })()}

              <Card
                title="ข้อมูลรอบตรวจ"
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>ชื่อรอบตรวจ:</Text>
                      <br />
                      <Text>{form.getFieldValue("name")}</Text>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>คำอธิบาย:</Text>
                      <br />
                      <Text>
                        {form.getFieldValue("description") || "ไม่มี"}
                      </Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>เวลา:</Text>
                      <br />
                      <Text>
                        {form.getFieldValue("start_time")?.format("HH:mm")} -{" "}
                        {form.getFieldValue("end_time")?.format("HH:mm")} (
                        {form.getFieldValue("estimated_duration")} นาที)
                      </Text>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>รปภ.ที่มอบหมาย:</Text>
                      <br />
                      <Text>{form.getFieldValue("guards_assigned")} คน</Text>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>วันที่ใช้งาน:</Text>
                      <br />
                      <Text>
                        {formatDaysShort(form.getFieldValue("days_of_week"))}
                      </Text>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card
                title={`จุดตรวจในรอบนี้ (${selectedCheckpoints.length} จุด)`}
                size="small"
              >
                <Space wrap>
                  {selectedCheckpoints.map((cpId, index) => {
                    const checkpoint = checkpoints.find((cp) => cp.id === cpId);
                    return (
                      <Tag key={cpId} color="blue" style={{ marginBottom: 8 }}>
                        {index + 1}. {checkpoint?.name}
                      </Tag>
                    );
                  })}
                </Space>
                {selectedCheckpoints.length === 0 && (
                  <Text type="secondary">ยังไม่ได้เลือกจุดตรวจ</Text>
                )}
              </Card>
            </div>
          )}

          <Divider />

          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>ยกเลิก</Button>
              {currentStep > 0 && <Button onClick={prevStep}>ย้อนกลับ</Button>}
              {currentStep < 2 ? (
                <Button type="primary" onClick={nextStep}>
                  ถัดไป
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={() => {
                    const startTime = form.getFieldValue("start_time");
                    const endTime = form.getFieldValue("end_time");
                    const overlap =
                      startTime && endTime
                        ? checkTimeOverlap(startTime, endTime)
                        : null;

                    if (overlap) {
                      message.error(
                        "ไม่สามารถบันทึกได้เนื่องจากเวลาทับซ้อนกัน",
                      );
                      return;
                    }

                    handleSubmit(form.getFieldsValue());
                  }}
                  disabled={(() => {
                    const startTime = form.getFieldValue("start_time");
                    const endTime = form.getFieldValue("end_time");
                    return startTime && endTime
                      ? checkTimeOverlap(startTime, endTime) !== null
                      : false;
                  })()}
                >
                  {editingRound ? "บันทึกการแก้ไข" : "สร้างรอบตรวจ"}
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PatrolRounds;
